/**
 * Cohort types for workshop cohort management
 */

// Cohort status
export type CohortStatus = "draft" | "active" | "completed" | "archived";

// Cohort member status
export type CohortMemberStatus = "pending" | "active" | "completed" | "removed";

/**
 * Cohort model
 */
export interface Cohort {
  id: string;
  name: string;
  description: string | null;
  workshopId: string | null;
  status: CohortStatus;
  startDate: string | null;
  endDate: string | null;
  maxParticipants: number | null;
  googleGroupEmail: string | null;
  inviteCode: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
}

/**
 * Cohort member model
 */
export interface CohortMember {
  id: string;
  cohortId: string;
  email: string;
  userId: string | null;
  status: CohortMemberStatus;
  enrolledAt: string;
  completedAt: string | null;
  enrolledBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create cohort request
 */
export interface CohortCreateRequest {
  name: string;
  description?: string;
  workshop_id?: string;
  status?: CohortStatus;
  start_date?: string;
  end_date?: string;
  max_participants?: number;
}

/**
 * Update cohort request
 */
export interface CohortUpdateRequest {
  name?: string;
  description?: string;
  workshop_id?: string;
  status?: CohortStatus;
  start_date?: string;
  end_date?: string;
  max_participants?: number;
}

/**
 * Add members request
 */
export interface CohortMemberBulkCreateRequest {
  emails: string[];
}

/**
 * Self-enrollment request
 */
export interface CohortEnrollRequest {
  email: string;
}

/**
 * Enrollment response
 */
export interface CohortEnrollResponse {
  success: boolean;
  message: string;
  cohortName: string;
  cohortId: string;
}

/**
 * Public cohort info from invite code
 */
export interface CohortInviteInfo {
  cohortName: string;
  description: string | null;
  workshopName: string | null;
  startDate: string | null;
  endDate: string | null;
  isFull: boolean;
}

/**
 * Google Group sync result
 */
export interface GoogleGroupSyncResult {
  added: number;
  removed: number;
  already_synced: number;
}

// API Response types (snake_case from backend)

export interface ApiCohort {
  id: string;
  name: string;
  description: string | null;
  workshop_id: string | null;
  status: CohortStatus;
  start_date: string | null;
  end_date: string | null;
  max_participants: number | null;
  google_group_email: string | null;
  invite_code: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  member_count: number;
}

export interface ApiCohortMember {
  id: string;
  cohort_id: string;
  email: string;
  user_id: string | null;
  status: CohortMemberStatus;
  enrolled_at: string;
  completed_at: string | null;
  enrolled_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiCohortEnrollResponse {
  success: boolean;
  message: string;
  cohort_name: string;
  cohort_id: string;
}

export interface ApiCohortInviteInfo {
  cohort_name: string;
  description: string | null;
  workshop_name: string | null;
  start_date: string | null;
  end_date: string | null;
  is_full: boolean;
}

// Transform functions

export function transformCohort(api: ApiCohort): Cohort {
  return {
    id: api.id,
    name: api.name,
    description: api.description,
    workshopId: api.workshop_id,
    status: api.status,
    startDate: api.start_date,
    endDate: api.end_date,
    maxParticipants: api.max_participants,
    googleGroupEmail: api.google_group_email,
    inviteCode: api.invite_code,
    createdBy: api.created_by,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    memberCount: api.member_count,
  };
}

export function transformCohortMember(api: ApiCohortMember): CohortMember {
  return {
    id: api.id,
    cohortId: api.cohort_id,
    email: api.email,
    userId: api.user_id,
    status: api.status,
    enrolledAt: api.enrolled_at,
    completedAt: api.completed_at,
    enrolledBy: api.enrolled_by,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function transformCohortEnrollResponse(
  api: ApiCohortEnrollResponse
): CohortEnrollResponse {
  return {
    success: api.success,
    message: api.message,
    cohortName: api.cohort_name,
    cohortId: api.cohort_id,
  };
}

export function transformCohortInviteInfo(
  api: ApiCohortInviteInfo
): CohortInviteInfo {
  return {
    cohortName: api.cohort_name,
    description: api.description,
    workshopName: api.workshop_name,
    startDate: api.start_date,
    endDate: api.end_date,
    isFull: api.is_full,
  };
}
